import { MetricClient, ClientOutput } from '../../Clients/MetricClient';
import { call, put } from 'redux-saga/effects';
import { ApiGetter } from '../../Clients/ApiGetter';
import { actions } from '../../../reducers/Dashboard/ContributionVolume';
import keyBy from 'lodash/keyBy';
import union from 'lodash/union';
import sortBy from 'lodash/sortBy';
import dayjs from 'dayjs';
import { Contribution } from '../../../reducers/Dashboard/ContributionVolume/Models';
import { DatedValue } from '../../Clients/MetricClient/Models/Metric';

export const metricClient = new MetricClient(new ApiGetter());

interface GetContributionVolumeAction {
  organizationUUID: string;
}

export function* getContributionVolume({
  organizationUUID,
}: GetContributionVolumeAction) {
  const linesAddedResponse: ClientOutput = yield call(
    [metricClient, metricClient.retrieveAsync],
    { organizationUUID, metric: 'total_additions', period: 30 }
  );

  const linesRemovedResponse: ClientOutput = yield call(
    [metricClient, metricClient.retrieveAsync],
    { organizationUUID, metric: 'total_deletions', period: 30 }
  );

  if (linesAddedResponse.error || linesRemovedResponse.error) {
    yield put(actions.setContributions(undefined));
    return;
  }

  if (
    linesAddedResponse.data === undefined &&
    linesRemovedResponse.data === undefined
  ) {
    yield put(actions.setContributions(undefined));
    return;
  }

  const contributions = getContributions(
    linesAddedResponse.data?.byDay,
    linesRemovedResponse.data?.byDay
  );

  yield put(actions.setContributions(contributions));
}

type DateAsKey = {
  [dateString: string]: DatedValue;
};

export function getContributions(
  added?: DatedValue[],
  removed?: DatedValue[]
): Contribution[] {
  const dateToAdded: DateAsKey = keyBy(added, (datedValue) =>
    dayjs(datedValue.date).format('YYYY-MM-DD')
  );

  const dateToRemoved: DateAsKey = keyBy(removed, (datedValue) =>
    dayjs(datedValue.date).format('YYYY-MM-DD')
  );

  const dateStrings = union(
    Object.keys(dateToAdded),
    Object.keys(dateToRemoved)
  );

  const contributions = dateStrings.map((dateString) => ({
    date: dayjs(dateString).toDate(),
    added: dateToAdded[dateString]?.value,
    removed: dateToRemoved[dateString]
      ? -1 * dateToRemoved[dateString].value // Removed lines are positive in API payload, but negative in view
      : undefined,
  }));

  return sortBy(contributions, 'date');
}
